import { useEffect, useRef, useState } from "react";
import { PlusCircle, Trash2, Upload } from "lucide-react";
import { api } from "@/lib/api";

const BACKEND_ORIGIN = process.env.REACT_APP_BACKEND_URL;

export default function AdminSettings() {
    const [form, setForm] = useState({
        instructor_name: "",
        instructor_initials: "",
        instructor_bio_markdown: "",
        instructor_bio_short: "",
        instructor_portrait_url: "",
        faq: [],
        legal_terms_markdown: "",
        legal_privacy_markdown: "",
        legal_risk_markdown: "",
        legal_updated_label: "",
    });
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [notice, setNotice] = useState(null);
    const [err, setErr] = useState(null);
    const [portraitBusy, setPortraitBusy] = useState(false);
    const portraitInputRef = useRef(null);

    async function load() {
        setLoading(true);
        try {
            const [admin, pub] = await Promise.all([api.adminGetSiteSettings(), api.publicSiteSettings()]);
            setForm({
                instructor_name: admin.instructor_name ?? pub.instructor_name ?? "",
                instructor_initials: admin.instructor_initials ?? pub.instructor_initials ?? "",
                instructor_bio_markdown: admin.instructor_bio_markdown ?? pub.instructor_bio_markdown ?? "",
                instructor_bio_short: admin.instructor_bio_short ?? pub.instructor_bio_short ?? "",
                instructor_portrait_url: admin.instructor_portrait_url ?? pub.instructor_portrait_url ?? "",
                faq: admin.faq ?? pub.faq ?? [],
                legal_terms_markdown: admin.legal_terms_markdown ?? pub.legal_terms_markdown ?? "",
                legal_privacy_markdown: admin.legal_privacy_markdown ?? pub.legal_privacy_markdown ?? "",
                legal_risk_markdown: admin.legal_risk_markdown ?? pub.legal_risk_markdown ?? "",
                legal_updated_label: admin.legal_updated_label ?? pub.legal_updated_label ?? "",
            });
        } finally { setLoading(false); }
    }
    useEffect(() => { load(); }, []);

    async function save() {
        setBusy(true); setNotice(null); setErr(null);
        try {
            if (!form.instructor_name?.trim() || !form.instructor_bio_markdown?.trim() || !form.instructor_bio_short?.trim()) {
                throw new Error("Instructor name, full bio, and short bio are all required.");
            }
            // Optional string fields: send `null` for empty strings so the backend
            // filter (`if v is not None` drops nothing, so empty string persists) does
            // not clobber default fallbacks. We use null (not undefined) to signal
            // "no change intended" — the backend excludes null via `exclude_none`.
            const optionalStrings = [
                "instructor_initials", "instructor_portrait_url",
                "legal_terms_markdown", "legal_privacy_markdown",
                "legal_risk_markdown", "legal_updated_label",
            ];
            const payload = { ...form };
            for (const k of optionalStrings) {
                if (typeof payload[k] === "string" && payload[k].trim() === "") payload[k] = null;
            }
            await api.adminUpdateSiteSettings(payload);
            setNotice("Settings saved.");
            // Refetch to prove persistence — the admin should see exactly what
            // the DB now has, not a stale client-side copy.
            await load();
        } catch (e) { setErr(e.message); }
        finally { setBusy(false); }
    }

    async function handlePortraitUpload(e) {
        const file = e.target.files?.[0];
        if (!file) return;
        setPortraitBusy(true); setErr(null); setNotice(null);
        try {
            const res = await api.adminUploadInstructorPortrait(file);
            setForm((f) => ({ ...f, instructor_portrait_url: res.instructor_portrait_url }));
            setNotice("Portrait uploaded.");
        } catch (ex) { setErr(ex.message || "Portrait upload failed."); }
        finally {
            setPortraitBusy(false);
            if (portraitInputRef.current) portraitInputRef.current.value = "";
        }
    }

    if (loading) return <div className="p-8 text-sm">Loading…</div>;

    const portraitSrc = form.instructor_portrait_url
        ? (form.instructor_portrait_url.startsWith("http") ? form.instructor_portrait_url : `${BACKEND_ORIGIN}${form.instructor_portrait_url}`)
        : null;

    return (
        <section className="max-w-4xl mx-auto px-6 py-10 space-y-6" data-testid="admin-settings-page">
            <div>
                <div className="tracking-caps text-[11px] font-semibold" style={{ color: "var(--st-royal)" }}>Settings</div>
                <h1 className="font-serif text-4xl mt-2" style={{ color: "var(--st-navy)" }}>Site content</h1>
                <p className="mt-2 text-sm" style={{ color: "var(--st-ink-muted)" }}>
                    Instructor bio, FAQ items, and legal page bodies. Changes go live immediately on the public site.
                </p>
            </div>

            {notice && <div className="rounded p-3 text-sm" style={{ background: "#ECFDF3", color: "#067647", border: "1px solid #ABEFC6" }} data-testid="settings-notice">{notice}</div>}
            {err && <div className="rounded p-3 text-sm" style={{ background: "#FEF3F2", color: "#B42318", border: "1px solid #FECDCA" }} data-testid="settings-error">{err}</div>}

            <div className="st-surface p-6 space-y-4" data-testid="settings-instructor">
                <h2 className="font-serif text-2xl" style={{ color: "var(--st-navy)" }}>Instructor</h2>
                <div className="grid md:grid-cols-[1fr_120px] gap-3">
                    <div>
                        <label className="st-label">Instructor name</label>
                        <input className="st-input" value={form.instructor_name}
                               onChange={(e) => setForm({ ...form, instructor_name: e.target.value })}
                               data-testid="settings-instructor-name" />
                    </div>
                    <div>
                        <label className="st-label">Initials (avatar)</label>
                        <input className="st-input" value={form.instructor_initials}
                               onChange={(e) => setForm({ ...form, instructor_initials: e.target.value })}
                               maxLength={4} data-testid="settings-instructor-initials" />
                    </div>
                </div>

                <div>
                    <label className="st-label">Portrait (png / jpg / webp, ≤ 5 MB)</label>
                    <div className="mt-2 flex items-center gap-4">
                        {portraitSrc ? (
                            <img src={portraitSrc} alt="Portrait"
                                 className="w-20 h-20 rounded-full object-cover"
                                 style={{ border: "1px solid var(--st-line)" }}
                                 data-testid="settings-instructor-portrait-preview" />
                        ) : (
                            <div className="w-20 h-20 rounded-full flex items-center justify-center"
                                 style={{ background: "var(--st-silver-100)", color: "var(--st-ink-muted)" }}>
                                <span className="font-serif text-xl">{(form.instructor_initials || "LC").slice(0, 4)}</span>
                            </div>
                        )}
                        <input ref={portraitInputRef} type="file"
                               accept="image/png,image/jpeg,image/webp,.png,.jpg,.jpeg,.webp"
                               className="hidden"
                               onChange={handlePortraitUpload}
                               data-testid="settings-instructor-portrait-input" />
                        <button type="button" className="btn-outline-navy text-xs inline-flex items-center gap-1"
                                onClick={() => portraitInputRef.current?.click()} disabled={portraitBusy}
                                data-testid="settings-instructor-portrait-upload">
                            <Upload size={12} /> {portraitBusy ? "Uploading…" : "Upload portrait"}
                        </button>
                    </div>
                </div>

                <div>
                    <label className="st-label">Short bio (footer / about card — one paragraph, 4-6 lines)</label>
                    <textarea rows={4} className="st-input" style={{ height: "auto", padding: "0.5rem 0.9rem" }}
                              value={form.instructor_bio_short}
                              onChange={(e) => setForm({ ...form, instructor_bio_short: e.target.value })}
                              data-testid="settings-instructor-bio-short" />
                </div>
                <div>
                    <label className="st-label">Full bio (markdown, paragraphs separated by blank lines)</label>
                    <textarea rows={12} className="st-input" style={{ height: "auto", padding: "0.5rem 0.9rem", fontFamily: "'Source Serif Pro', Georgia, serif" }}
                              value={form.instructor_bio_markdown}
                              onChange={(e) => setForm({ ...form, instructor_bio_markdown: e.target.value })}
                              data-testid="settings-instructor-bio" />
                    {form.instructor_bio_markdown && (
                        <div className="mt-3 p-4 rounded" style={{ background: "var(--st-silver-50)", border: "1px solid var(--st-line)" }} data-testid="settings-instructor-bio-preview">
                            <div className="tracking-caps text-[10px] font-semibold mb-2" style={{ color: "var(--st-silver-500)" }}>Preview</div>
                            {form.instructor_bio_markdown.split(/\n\s*\n/).filter(Boolean).map((para, i) => (
                                <p key={i} className="text-sm leading-relaxed" style={{ color: "var(--st-ink)", marginTop: i === 0 ? 0 : "0.75rem" }}>{para}</p>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            <div className="st-surface p-6 space-y-4" data-testid="settings-faq">
                <h2 className="font-serif text-2xl" style={{ color: "var(--st-navy)" }}>FAQ</h2>
                {form.faq.map((item, idx) => (
                    <div key={idx} className="grid gap-2 p-3 rounded" style={{ background: "var(--st-silver-50)", border: "1px solid var(--st-line)" }}
                         data-testid={`faq-item-${idx}`}>
                        <input className="st-input" value={item.q}
                               onChange={(e) => setForm({ ...form, faq: form.faq.map((f, i) => i === idx ? { ...f, q: e.target.value } : f) })}
                               placeholder="Question" />
                        <textarea rows={3} className="st-input" style={{ height: "auto", padding: "0.5rem 0.9rem" }}
                                  value={item.a}
                                  onChange={(e) => setForm({ ...form, faq: form.faq.map((f, i) => i === idx ? { ...f, a: e.target.value } : f) })}
                                  placeholder="Answer" />
                        <button type="button" className="btn-ghost text-xs inline-flex items-center gap-1 self-start"
                                onClick={() => setForm({ ...form, faq: form.faq.filter((_, i) => i !== idx) })}
                                data-testid={`faq-delete-${idx}`}>
                            <Trash2 size={12} /> Remove
                        </button>
                    </div>
                ))}
                <button type="button" className="btn-outline-navy text-xs inline-flex items-center gap-1"
                        onClick={() => setForm({ ...form, faq: [...form.faq, { q: "", a: "" }] })}
                        data-testid="faq-add-btn">
                    <PlusCircle size={12} /> Add FAQ item
                </button>
            </div>

            <div className="st-surface p-6 space-y-4" data-testid="settings-legal">
                <h2 className="font-serif text-2xl" style={{ color: "var(--st-navy)" }}>Legal pages</h2>
                <div>
                    <label className="st-label">"Last updated" label (shown on all legal pages)</label>
                    <input className="st-input" value={form.legal_updated_label}
                           onChange={(e) => setForm({ ...form, legal_updated_label: e.target.value })}
                           placeholder="February 2026" data-testid="settings-legal-updated" />
                </div>
                {[
                    { key: "legal_terms_markdown", label: "Terms of Service (markdown)" },
                    { key: "legal_privacy_markdown", label: "Privacy Policy (markdown)" },
                    { key: "legal_risk_markdown", label: "Risk Disclosure (markdown)" },
                ].map((f) => (
                    <div key={f.key}>
                        <label className="st-label">{f.label}</label>
                        <textarea rows={8} className="st-input" style={{ height: "auto", padding: "0.5rem 0.9rem" }}
                                  value={form[f.key]}
                                  onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                                  placeholder="Leave blank to use the built-in default."
                                  data-testid={`settings-${f.key}`} />
                    </div>
                ))}
            </div>

            <div>
                <button type="button" className="btn-royal text-sm" onClick={save} disabled={busy} data-testid="settings-save">
                    {busy ? "Saving…" : "Save all settings"}
                </button>
            </div>
        </section>
    );
}
